import React, { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    List,
    ListItemButton,
    ListItemText,
    TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';

import { auth } from '../../../firebase.js';
import {
    deleteWelcomeEmailTemplate,
    saveWelcomeEmailTemplate,
} from '../../../common/welcomeEmailApi.js';
import { templateProblems } from '../../../common/welcomeEmailTemplate.js';
import WelcomeEmailPreview from './WelcomeEmailPreview.jsx';

const NEW_TEMPLATE = {
    name: '',
    subject: 'Din bruger til ARIS Insights',
    reply_to: '',
    body: [
        'Hej {{name}}',
        '',
        'Der er oprettet en bruger til dig hos ARIS Robotics med emailadressen {{email}}.',
        '',
        'Vælg din adgangskode her:',
        '{{link}}',
        '',
        'Linket virker i 14 dage.',
        '',
        'Venlig hilsen',
        'ARIS Robotics',
    ].join('\n'),
};

const SAMPLE_RECIPIENT = { name: 'Mette Jensen', email: 'mette.jensen@eksempel.dk' };

const draftFrom = (template) => ({
    id: template.id,
    name: template.name || '',
    subject: template.subject || '',
    reply_to: template.reply_to || '',
    body: template.body || '',
});

const WelcomeEmailTemplatesDialog = ({ open, onClose, templates, loading, loadError, showMessage }) => {
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => {
        if (!open) {
            setDraft(null);
            setConfirmDelete(false);
        }
    }, [open]);

    const problems = draft ? templateProblems(draft) : [];
    const busy = saving || deleting;

    const updateDraft = (key) => (event) => {
        const { value } = event.target;
        setDraft((current) => ({ ...current, [key]: value }));
    };

    const save = async () => {
        if (problems.length > 0) return;
        setSaving(true);
        try {
            const id = await saveWelcomeEmailTemplate(draft, auth.currentUser?.uid);
            setDraft((current) => ({ ...current, id }));
            showMessage('Skabelonen er gemt.');
        } catch (error) {
            console.error('Error saving welcome email template:', error);
            showMessage('Kunne ikke gemme skabelonen.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        setDeleting(true);
        try {
            await deleteWelcomeEmailTemplate(draft.id);
            setDraft(null);
            setConfirmDelete(false);
            showMessage('Skabelonen er slettet.');
        } catch (error) {
            console.error('Error deleting welcome email template:', error);
            showMessage('Kunne ikke slette skabelonen.', 'error');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            <Dialog open={open} onClose={busy ? undefined : onClose} className="setup-dialog" maxWidth="lg" fullWidth>
                <DialogTitle>E-mailskabeloner</DialogTitle>
                <DialogContent className="welcome-templates-layout">
                    <div className="welcome-templates-list">
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => setDraft({ ...NEW_TEMPLATE })}
                            disabled={busy}
                        >
                            Ny skabelon
                        </Button>
                        {loadError && <Alert severity="error">{loadError}</Alert>}
                        {!loading && templates.length === 0 && (
                            <p className="empty-inline">Ingen skabeloner endnu.</p>
                        )}
                        <List dense disablePadding>
                            {templates.map((template) => (
                                <ListItemButton
                                    key={template.id}
                                    selected={draft?.id === template.id}
                                    onClick={() => setDraft(draftFrom(template))}
                                    disabled={busy}
                                >
                                    <ListItemText primary={template.name} secondary={template.subject} />
                                </ListItemButton>
                            ))}
                        </List>
                    </div>

                    {draft ? (
                        <div className="welcome-templates-editor">
                            <TextField
                                label="Navn"
                                helperText="Kun synligt her i Setup."
                                value={draft.name}
                                onChange={updateDraft('name')}
                                size="small"
                                disabled={busy}
                            />
                            <TextField
                                label="Emne"
                                value={draft.subject}
                                onChange={updateDraft('subject')}
                                size="small"
                                disabled={busy}
                            />
                            <TextField
                                label="Svar til"
                                helperText="Svar på mailen går hertil, da service@aris-robotics.com sjældent læses."
                                value={draft.reply_to}
                                onChange={updateDraft('reply_to')}
                                size="small"
                                disabled={busy}
                            />
                            <TextField
                                label="Tekst"
                                helperText="Pladsholdere: {{name}}, {{email}} og {{link}}. {{link}} skal være med."
                                value={draft.body}
                                onChange={updateDraft('body')}
                                multiline
                                minRows={10}
                                disabled={busy}
                                inputProps={{ spellCheck: false }}
                            />
                            {problems.length > 0 && (
                                <Alert severity="warning">
                                    <ul className="welcome-problem-list">
                                        {problems.map((problem) => (
                                            <li key={problem}>{problem}</li>
                                        ))}
                                    </ul>
                                </Alert>
                            )}
                            <div className="welcome-templates-editor-actions">
                                {draft.id && (
                                    <Button
                                        color="error"
                                        startIcon={<DeleteIcon />}
                                        onClick={() => setConfirmDelete(true)}
                                        disabled={busy}
                                    >
                                        Slet
                                    </Button>
                                )}
                                <Button
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    onClick={save}
                                    disabled={busy || problems.length > 0}
                                >
                                    {draft.id ? 'Gem' : 'Opret skabelon'}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <p className="empty-inline welcome-templates-placeholder">
                            Vælg en skabelon, eller opret en ny.
                        </p>
                    )}

                    {draft && (
                        <div className="welcome-templates-preview">
                            <h3>Forhåndsvisning</h3>
                            <WelcomeEmailPreview template={draft} {...SAMPLE_RECIPIENT} />
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={busy}>
                        Luk
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} className="setup-dialog">
                <DialogTitle>Slet skabelon?</DialogTitle>
                <DialogContent>
                    <DialogContentText className="welcome-dialog-text">
                        "{draft?.name}" slettes. Mails, der allerede er sendt, påvirkes ikke.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDelete(false)} disabled={deleting}>
                        Annuller
                    </Button>
                    <Button variant="contained" color="error" onClick={remove} disabled={deleting}>
                        Slet
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default WelcomeEmailTemplatesDialog;
